// js/Component/AddressBook/EditContactModal.jsx
import React, { useState } from "react";
import toast from "react-hot-toast";
import { updateAddressBookEntry, uploadEntryAvatar } from "js/Api/index";
import { validateName, validatePhoneNumber } from "js/helpers/common_functions";

function EditContactModal({ entry, onClose, onUpdated }) {
    const isVideo = entry.entryType === "video_contact";

    const [displayName, setDisplayName] = useState(entry.displayName || "");
    const [phoneNumberRaw, setPhoneNumberRaw] = useState(entry.phoneNumberRaw || "");
    const [file, setFile] = useState(null);
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        const nameResult = validateName(displayName);
        if (!nameResult.valid) {
            toast.error(nameResult.error);
            return;
        }
        const name = nameResult.value;

        let phone = "";
        if (!isVideo) {
            const phoneResult = validatePhoneNumber(phoneNumberRaw);
            if (!phoneResult.valid) {
                toast.error(phoneResult.error);
                return;
            }
            phone = phoneResult.value;
        }

        setSaving(true);

        const body = { displayName: name };
        if (!isVideo) {
            body.phoneNumberRaw = phone;
        }

        const updateRes = await updateAddressBookEntry(entry.id, body);
        if (!updateRes.success) {
            setSaving(false);
            toast.error(updateRes.error || "Failed to update contact");
            return;
        }

        let finalEntry = updateRes.data;

        if (file && !isVideo) {
            const avatarRes = await uploadEntryAvatar(entry.id, file);
            if (!avatarRes.success) {
                setSaving(false);
                toast.error(avatarRes.error || "Contact saved but avatar upload failed");
                onUpdated(finalEntry);
                onClose();
                return;
            }
            finalEntry = avatarRes.data || finalEntry;
        }

        setSaving(false);
        toast.success("Contact updated");
        onUpdated(finalEntry);
        onClose();
    };

    return (
        <div className="modal" role="dialog" style={{ display: "block" }}>
            <div className="modal-dialog" role="document">
                <div className="modal-content" style={{ width: 460 }}>
                    <div className="modal-header">
                        <h1 className="modal-title">
                            Edit {isVideo ? "Video" : "Phone"} Contact
                        </h1>
                        <button
                            className="close-button"
                            onClick={onClose}
                            disabled={saving}
                        >
                            &times;
                        </button>
                    </div>
                    <div className="modal-body">
                        <div style={{ marginBottom: 12 }}>
                            <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 4, display: "block" }}>
                                Name <span style={{ color: "#c44" }}>*</span>
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                value={displayName}
                                onChange={(e) => setDisplayName(e.target.value)}
                                disabled={saving}
                                autoFocus
                            />
                        </div>

                        {!isVideo && (
                            <>
                                <div style={{ marginBottom: 12 }}>
                                    <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 4, display: "block" }}>
                                        Phone Number
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={phoneNumberRaw}
                                        onChange={(e) => setPhoneNumberRaw(e.target.value)}
                                        disabled={saving}
                                        placeholder="+61 400 123 456"
                                    />
                                    <div
                                        style={{
                                            fontSize: 12,
                                            color: "#666",
                                            marginTop: 4,
                                        }}
                                    >
                                        Include country code, e.g. +61 400 123 456
                                    </div>
                                </div>

                                <div style={{ marginBottom: 4 }}>
                                    <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 4, display: "block" }}>
                                        Avatar (optional — leave blank to keep current)
                                    </label>
                                    <input
                                        id="address-book-edit-avatar"
                                        type="file"
                                        accept="image/png,image/jpeg"
                                        onChange={(e) => setFile(e.target.files?.[0] || null)}
                                        disabled={saving}
                                        style={{ display: "none" }}
                                    />
                                    <label
                                        htmlFor="address-book-edit-avatar"
                                        className="btn btn-sm btn-outline-secondary"
                                        style={{ marginBottom: 0, cursor: saving ? "not-allowed" : "pointer" }}
                                    >
                                        {file ? "Change file" : "Choose file"}
                                    </label>
                                    {file && (
                                        <div
                                            style={{
                                                fontSize: 12,
                                                color: "#666",
                                                marginTop: 6,
                                                wordBreak: "break-all",
                                            }}
                                        >
                                            {file.name}
                                        </div>
                                    )}
                                </div>
                            </>
                        )}

                        {isVideo && (
                            <p style={{ fontSize: 12, color: "#666", margin: 0 }}>
                                Video contacts are managed automatically by the system. Only the
                                display name can be edited.
                            </p>
                        )}
                    </div>
                    <div className="modal-footer">
                        <button
                            type="button"
                            className="btn cur-p btn-secondary"
                            onClick={onClose}
                            disabled={saving}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn cur-p btn-primary"
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? "Saving..." : "Save"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default EditContactModal;
